import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { routeOptions, guides } from '../data/admissionData';
import {
  examplesForProfile,
  factChips,
  matchRoutes,
  resourcesForProfile,
} from '../data/routeProfiles';
import { AnalyticsEvents, trackEvent } from '../utils/analytics';
import { usePageMeta } from '../hooks/usePageMeta';
import '../styles/Finder.css';
import '../styles/Resources.css';

const stageLabel = Object.fromEntries(routeOptions.stages.map((s) => [s.id, s.label]));
const guideTitle = Object.fromEntries(guides.map((g) => [g.id, g.title]));

const Finder = () => {
  const [track, setTrack] = useState('eju');
  const [field, setField] = useState('stem');
  const [stage, setStage] = useState('explore');

  usePageMeta({
    title: '루트 찾기',
    description:
      '전형·계열·단계에 맞춰 EJU·일반·국비 루트와 공식 링크를 찾습니다. 최종은 모집요항 원문.',
    path: '/finder',
  });

  const results = useMemo(() => matchRoutes(track, field, stage), [track, field, stage]);

  useEffect(() => {
    if (results.length === 0) return;
    trackEvent(AnalyticsEvents.FINDER_COMPLETE, {
      track,
      field,
      stage,
      count: results.length,
    });
  }, [track, field, stage, results.length]);

  return (
    <div className="apple-page apple-finder">
      <header className="apple-page-hero">
        <p className="apple-eyebrow">Route finder</p>
        <h1 className="apple-display">루트 찾기</h1>
        <p className="apple-lede">
          전형·계열·단계에 맞춰 큐레이션된 루트, 공식 링크, 대학 입시 페이지 예시를 보여 줍니다.
          최종 확인은 해당 연도 모집요항 원문입니다.
        </p>
      </header>

      <form className="apple-finder-form" onSubmit={(e) => e.preventDefault()}>
        {[
          { legend: '전형', name: 'track', value: track, set: setTrack, options: routeOptions.tracks },
          { legend: '전공', name: 'field', value: field, set: setField, options: routeOptions.fields },
          { legend: '단계', name: 'stage', value: stage, set: setStage, options: routeOptions.stages },
        ].map((group) => (
          <fieldset key={group.name}>
            <legend>{group.legend}</legend>
            <div className="apple-segment">
              {group.options.map((option) => (
                <label key={option.id} className={group.value === option.id ? 'is-on' : ''}>
                  <input
                    type="radio"
                    name={group.name}
                    value={option.id}
                    checked={group.value === option.id}
                    onChange={() => group.set(option.id)}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </fieldset>
        ))}
      </form>

      <section className="apple-finder-results" aria-live="polite">
        <h2>
          결과 <span>{results.length}</span>
        </h2>
        {results.length === 0 ? (
          <p className="apple-empty">이 조합의 데이터가 아직 없습니다.</p>
        ) : (
          <ul className="apple-finder-cards">
            {results.map(({ route, matchedStage, fallback }) => {
              const links = resourcesForProfile(route);
              const examples = examplesForProfile(route);
              const chips = factChips(route.facts);
              const steps =
                route.nextStepsByStage?.[matchedStage] ||
                route.nextStepsByStage?.[stage] ||
                [];

              return (
                <li key={route.id} className="apple-finder-card">
                  <div className="apple-finder-card-head">
                    <h3>{route.title}</h3>
                    {fallback && (
                      <p className="apple-finder-fallback">
                        선택 단계에 맞춘 안내가 없어 「{stageLabel[matchedStage]}」 기준으로
                        표시합니다.
                      </p>
                    )}
                  </div>
                  <p className="apple-finder-summary">{route.summary}</p>

                  {chips.length > 0 && (
                    <ul className="apple-fact-chips">
                      {chips.map((chip) => (
                        <li key={chip}>{chip}</li>
                      ))}
                    </ul>
                  )}

                  {route.facts?.ejuSubjectsHint && (
                    <p className="apple-finder-hint">
                      <strong>과목 힌트</strong> {route.facts.ejuSubjectsHint}
                    </p>
                  )}

                  <h4>이 단계의 다음 행동</h4>
                  <ol>
                    {steps.map((step) => (
                      <li key={step}>{step}</li>
                    ))}
                  </ol>

                  {links.length > 0 && (
                    <>
                      <h4>공식 링크</h4>
                      <ul className="apple-route-links">
                        {links.map((item) => (
                          <li key={item.id}>
                            <a href={item.url} target="_blank" rel="noopener noreferrer">
                              {item.name}
                            </a>
                            <span>{item.org}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  {examples.length > 0 && (
                    <>
                      <h4>대학·기관 예시</h4>
                      <ul className="apple-example-list">
                        {examples.map((ex) => (
                          <li key={ex.id}>
                            <a href={ex.admissionUrl} target="_blank" rel="noopener noreferrer">
                              {ex.univ}
                            </a>
                            <span className="apple-example-meta">
                              {ex.facultyHint} · 확인 {ex.verifiedAt}
                            </span>
                            <p>{ex.notes}</p>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  {route.guideIds?.length > 0 && (
                    <p className="apple-finder-guides">
                      관련:{' '}
                      {route.guideIds.map((gid, i) => (
                        <React.Fragment key={gid}>
                          {i > 0 ? ' · ' : null}
                          <Link to={`/guides/${gid}`}>{guideTitle[gid] || gid}</Link>
                        </React.Fragment>
                      ))}
                      {' · '}
                      <Link to="/checklist">요강 체크리스트</Link>
                    </p>
                  )}

                  <p className="apple-finder-disclaimer">{route.disclaimer}</p>
                  <p className="apple-finder-updated">업데이트 {route.updatedAt}</p>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <div className="apple-page-foot">
        <Link to="/checklist" className="apple-btn apple-btn-primary">
          요강 체크리스트
        </Link>
        <Link to="/compare" className="apple-link">
          대학 비교
        </Link>
        <Link to="/resources" className="apple-link">
          공식 자료 표
        </Link>
        <Link to="/universities" className="apple-link">
          대학 입시 안내
        </Link>
        <Link to="/guides" className="apple-link">
          가이드
        </Link>
      </div>
    </div>
  );
};

export default Finder;
